import { FC } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pushGtmEvent, gtmEvents } from '../../utils/gtm';

const Footer: FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-content">
          {/* Logo and Company Info */}
          <div className="footer-section footer-brand">
            <Link to="/">
              <img
                src="/MSC-GROUP-WHITE-LOGO.svg"
                alt="MSC Certifications"
                className="footer-logo"
              />
            </Link>
            <p className="footer-description">
              {t('footer.companyDescription')}
            </p>
          </div>

          {/* Quick Links */}
          <div className="footer-section">
            <h3>{t('footer.quickLinks')}</h3>
            <ul className="footer-links">
              <li><Link to="/about-us/">{t('nav.aboutUs')}</Link></li>
              <li><Link to="/services">{t('nav.ourServices')}</Link></li>
              <li><Link to="/contact">{t('footer.contact')}</Link></li>
              <li><Link to="/faq">FAQ</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div className="footer-section">
            <h3>{t('footer.services')}</h3>
            <ul className="footer-links">
              <li><Link to="/services/iso">{t('nav.isoCertifications')}</Link></li>
              <li><Link to="/services/ce-marking">{t('nav.ceMarking')}</Link></li>
              <li><Link to="/services/iso/haccp">{t('nav.haccp')}</Link></li>
              <li><Link to="/services/additional/energy-efficiency">{t('nav.energyEfficiency')}</Link></li>
              <li><Link to="/services/additional/staff-training">{t('nav.staffTraining')}</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="footer-section">
            <h3>{t('nav.contactUs')}</h3>
            <ul className="footer-contact">
              <li>
                <span className="contact-label">Email:</span>
                <a href="mailto:info@msc-cert.com" onClick={() => pushGtmEvent(gtmEvents.emailClick, { location: 'footer', email: 'info@msc-cert.com' })}>info@msc-cert.com</a>
              </li>
              <li>
                <span className="contact-label">{t('footer.phone')}:</span>
                <a href="tel:+355672063632" onClick={() => pushGtmEvent(gtmEvents.phoneClick, { location: 'footer', phone: '+355672063632' })}>+355 67 206 3632</a>
              </li>
              <li>
                <span className="contact-label">{t('footer.address')}:</span>
                <span>Str. Ismail Qemali, Tirana, Albania</span>
              </li>
            </ul>
            <div className="footer-social">
              <a href="https://www.instagram.com/msc_certifications" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </a>
              <a href="https://www.linkedin.com/company/msc-certifications/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="footer-bottom">
          <p>&copy; {currentYear} MSC Certifications. {t('footer.allRightsReserved')}</p>
          <div className="footer-bottom-links">
            <Link to="/privacy-policy">{t('footer.privacyPolicy')}</Link>
            <Link to="/terms-and-conditions">{t('footer.termsConditions')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;